import { googleLogout } from "@react-oauth/google";
import { useCallback, useContext, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { ReactComponent as Logo } from "../../assets/logo/logo_text_white.svg";
import { AuthContext } from "../../context/AuthContext";
import { PageEndpointEnum } from "../../enums/endpoints";
import { hasPageAccess } from "../../constants/permissions";
import {
  Divider,
  HeaderRow,
  LeftHeaderWrapper,
  LinkText,
  LogoBlock,
  MainHeaderWrapper,
  NavigationRow,
  RightHeaderWrapper,
} from "./style";
import { ToastEnum, ToastStatusEnum, useToast } from "../../hooks/toast";
import { ReactComponent as Copy } from "../../assets/svg/copy.svg";
import { Button, ButtonThemeEnum } from "../shared/Buttons/styles";
import { COLORS } from "../../constants/design/colors";
import { SPACING } from "../../constants/design/spacing";
import { isAffiliateEnvironment } from "../../utils/env";

import { makerEnvironment } from "../Makers/environment";

interface ILink {
  title: string;
  to: string;
  isActive: boolean;
  isHidden?: boolean;
}

export function Header({
  showSessionActions = true,
}: { showSessionActions?: boolean } = {}) {
  const location = useLocation();
  const { tokenId, setTokenId, userEmail } = useContext(AuthContext);
  const { addToast } = useToast();

  const exchangeLinks: ILink[] = useMemo(() => {
    const links: ILink[] = [
      {
        title: "Dashboard",
        to: PageEndpointEnum.DASHBOARD,
        isActive: location.pathname === "/",
      },
      {
        title: "Book",
        to: PageEndpointEnum.BOOK,
        isActive: location.pathname === "/book",
      },
      {
        title: "Withdrawals",
        to: PageEndpointEnum.WITHDRAW,
        isActive: location.pathname === "/withdraw",
      },
      {
        title: "Risk",
        to: PageEndpointEnum.RISK,
        isActive: location.pathname === "/risk",
      },
      {
        title: "Isolated Risk",
        to: PageEndpointEnum.ISOLATED_RISK_MONITOR,
        isActive: location.pathname === "/isolated-risk-monitor",
      },
      {
        title: "Settings",
        to: PageEndpointEnum.SETTINGS,
        isActive: location.pathname === "/settings",
      },
      {
        title: "Geo",
        to: PageEndpointEnum.GEOLOCATIONS,
        isActive: location.pathname === PageEndpointEnum.GEOLOCATIONS,
      },
      {
        title: "Transactions",
        to: PageEndpointEnum.TX,
        isActive: location.pathname === "/tx",
      },
      {
        title: "Live Trades",
        to: PageEndpointEnum.LIVE_TRADES,
        isActive: location.pathname === "/live-trades",
      },
      {
        title: "Debug",
        to: PageEndpointEnum.DEBUG,
        isActive: location.pathname === "/debug",
      },
      {
        title: "Volume",
        to: PageEndpointEnum.VOLUME,
        isActive: location.pathname === "/volume",
      },
      {
        title: "Security",
        to: PageEndpointEnum.SECURITY,
        isActive: location.pathname === "/security",
      },
      {
        title: "Ondo",
        to: PageEndpointEnum.ONDO,
        isActive: location.pathname === "/ondo",
      },
      {
        title: "Builders",
        to: PageEndpointEnum.BUILDERS,
        isActive: location.pathname === "/builders",
      },
      {
        title: "Strategies",
        to: PageEndpointEnum.STRATEGIES,
        isActive: location.pathname === "/strategies",
      },
      {
        title: "Rewards Program",
        to: PageEndpointEnum.REWARDS,
        isActive: location.pathname === PageEndpointEnum.REWARDS,
      },
      ...(makerEnvironment()
        ? [
            {
              title: "Makers",
              to: PageEndpointEnum.MAKERS,
              isActive: location.pathname === PageEndpointEnum.MAKERS,
            },
          ]
        : []),
      ...(isAffiliateEnvironment()
        ? [
            {
              title: "Affiliates",
              to: PageEndpointEnum.AFFILIATES,
              isActive: location.pathname === PageEndpointEnum.AFFILIATES,
            },
          ]
        : []),
    ];

    // Filter links based on permissions
    return links.map((link) => ({
      ...link,
      isHidden: !hasPageAccess(userEmail, link.to as PageEndpointEnum),
    }));
  }, [location.pathname, userEmail]);

  const mmLinks: ILink[] = useMemo(() => {
    const links: ILink[] = [
      {
        title: "MM Monitoring",
        to: PageEndpointEnum.MM_MONITORING,
        isActive: location.pathname === "/mm-monitoring",
      },
      {
        title: "MM Control Panel",
        to: PageEndpointEnum.MM_CONTROL_PANEL,
        isActive: location.pathname === "/mm-control-panel",
      },
      {
        title: "Trading Parameters",
        to: PageEndpointEnum.TRADING_PARAMETERS,
        isActive: location.pathname === "/trading-parameters",
      },
      {
        title: "Equity",
        to: PageEndpointEnum.EQUITY,
        isActive: location.pathname === "/equity",
      },
      {
        title: "PnL",
        to: PageEndpointEnum.PNL,
        isActive: location.pathname === "/pnl",
      },
      {
        title: "Perp Data",
        to: PageEndpointEnum.PERP_DATA,
        isActive: location.pathname === "/perp-data",
      },
      {
        title: "MM Trades",
        to: PageEndpointEnum.MMSTRATEGIES,
        isActive: location.pathname === "/mm-strategies",
      },
      {
        title: "Options Trades",
        to: PageEndpointEnum.OPTIONSTRADES,
        isActive: location.pathname === "/options-trades",
        isHidden: true,
      },
      {
        title: "MM Trades (Old)",
        to: PageEndpointEnum.MMTRADES,
        isActive: location.pathname === "/mm-trades",
        isHidden: true,
      },
    ];

    // Filter links based on permissions
    return links.map((link) => ({
      ...link,
      isHidden:
        link.isHidden || !hasPageAccess(userEmail, link.to as PageEndpointEnum),
    }));
  }, [location.pathname, userEmail]);

  const onCopy = useCallback(async () => {
    if (tokenId) {
      await navigator.clipboard.writeText(tokenId);
      addToast(
        {
          type: ToastEnum.SIMPLE,
          header: "Bearer Token Copied",
          status: ToastStatusEnum.SUCCESS,
        },
        4000
      );
    }
  }, [addToast, tokenId]);

  return (
    <HeaderRow>
      <div>
        <MainHeaderWrapper>
          <LogoBlock>
            <Link to={PageEndpointEnum.DASHBOARD}>
              <Logo height={20} width={60} />
            </Link>
            <Divider height="60%" horizontalMargin={`${SPACING.three}px`} />
          </LogoBlock>
          <LeftHeaderWrapper>
            <NavigationRow>
              {exchangeLinks.map((link, i) => {
                if (!link.isHidden) {
                  return (
                    <Link key={i} to={link.to}>
                      <LinkText isActive={link.isActive}>{link.title}</LinkText>
                    </Link>
                  );
                }
                return null;
              })}
            </NavigationRow>
            <NavigationRow>
              {mmLinks.map((link, i) => {
                if (!link.isHidden) {
                  return (
                    <Link key={i} to={link.to}>
                      <LinkText isActive={link.isActive}>{link.title}</LinkText>
                    </Link>
                  );
                }
                return null;
              })}
            </NavigationRow>
          </LeftHeaderWrapper>
          {showSessionActions && (
            <RightHeaderWrapper>
              <Button
                style={{ marginRight: SPACING.two }}
                buttonTheme={ButtonThemeEnum.HIGHLIGHT}
                onClick={onCopy}
              >
                <Copy
                  style={{
                    stroke: COLORS.highlight.one,
                    marginRight: SPACING.two,
                  }}
                />{" "}
                Copy Token
              </Button>
              <Button
                buttonTheme={ButtonThemeEnum.SYSTEM}
                onClick={() => {
                  googleLogout();
                  window.localStorage.removeItem("tokenId");
                  setTokenId(undefined);
                }}
              >
                Logout
              </Button>
            </RightHeaderWrapper>
          )}
        </MainHeaderWrapper>
      </div>
    </HeaderRow>
  );
}
